/**
 * Patient-reported outcome (PRO) instruments (`BACKLOG.md` M7). **Licensing, not accuracy, is the
 * reason this module is shaped the way it is — read this header before adding anything to it.**
 *
 * PROMIS (Patient-Reported Outcomes Measurement Information System) is NIH-developed and genuinely
 * free/public-domain for clinical and research use (HealthMeasures/PROMIS's own terms). **KOOS/HOOS
 * (Knee/Hip injury and Osteoarthritis Outcome Score) and DASH/QuickDASH (Disabilities of the Arm,
 * Shoulder and Hand) are commercially licensed** — confirmed directly per this task's own brief, not
 * assumed free. Both facts are real and matter, but neither is why this module stores no instrument
 * *content* (item text, scoring algorithms, population norms) for **any** instrument, PROMIS included:
 *
 *   1. **The licensed instruments genuinely cannot be embedded here at all** without a license this
 *      codebase does not have — copying KOOS/HOOS or DASH item text or scoring logic into this repo
 *      would be a real copyright violation, not a style choice.
 *   2. **PROMIS's item content, while legally embeddable, is not embedded either** — fabricating item
 *      wording or a scoring algorithm from memory risks getting it clinically wrong in a way distinct
 *      from a licensing problem, the same accuracy risk `examManeuvers.ts`'s and `conditionTemplates.ts`'s
 *      own UNREVIEWED markers name. A future pass that sources real, verified PROMIS content (the
 *      HealthMeasures scoring manuals, cited) can embed it for PROMIS specifically at that point —
 *      nothing here structurally prevents that — but guessing at it now would be worse than not
 *      building it.
 *
 * So this module — and `orthopedicsPatientReportedOutcomes` in `db/schema/orthopedics.ts` — is
 * deliberately generic: it stores *which* instrument was administered, *when*, and the score a
 * clinician entered, the same way any other structured clinical value is stored (`vitals.value`'s own
 * "value plus a type tag" shape). It never renders an item, never asks a scoring question, and never
 * computes a score from raw item responses for any instrument. `licenseStatus` is carried on every
 * instrument specifically so a UI can display the distinction this header states, rather than a caller
 * having to know it out of band.
 */

export const PRO_INSTRUMENT_LICENSE_STATUSES = ['public_domain', 'commercially_licensed'] as const;
export type ProInstrumentLicenseStatus = (typeof PRO_INSTRUMENT_LICENSE_STATUSES)[number];

export interface ProInstrumentDescriptor {
  readonly id: string;
  readonly label: string;
  readonly licenseStatus: ProInstrumentLicenseStatus;
}

/**
 * The instrument catalog. `koos`/`hoos`/`dash`/`quickdash` are listed as real, named instruments a
 * clinician may record a score for — this module never embeds their content, only their existence and
 * license posture — plus `promis_global10`, PROMIS's free short-form global-health instrument, listed
 * the same way. `other` covers anything not in this small list, matching `rcm.ts`'s own "small curated
 * catalog, not a licensed database" reasoning for a different closed-but-extensible set.
 */
export const PRO_INSTRUMENTS: readonly ProInstrumentDescriptor[] = [
  { id: 'promis_global10', label: 'PROMIS Global-10', licenseStatus: 'public_domain' },
  { id: 'promis_pf10a', label: 'PROMIS Physical Function 10a', licenseStatus: 'public_domain' },
  {
    id: 'koos',
    label: 'KOOS (Knee injury and Osteoarthritis Outcome Score)',
    licenseStatus: 'commercially_licensed',
  },
  {
    id: 'hoos',
    label: 'HOOS (Hip disability and Osteoarthritis Outcome Score)',
    licenseStatus: 'commercially_licensed',
  },
  {
    id: 'dash',
    label: 'DASH (Disabilities of the Arm, Shoulder and Hand)',
    licenseStatus: 'commercially_licensed',
  },
  { id: 'quickdash', label: 'QuickDASH', licenseStatus: 'commercially_licensed' },
  { id: 'other', label: 'Other / not listed', licenseStatus: 'commercially_licensed' },
] as const;

export const PRO_INSTRUMENT_IDS = PRO_INSTRUMENTS.map((instrument) => instrument.id) as [
  string,
  ...string[],
];

export function isProInstrumentId(value: string): boolean {
  return PRO_INSTRUMENT_IDS.includes(value);
}

export function proInstrumentById(id: string): ProInstrumentDescriptor | undefined {
  return PRO_INSTRUMENTS.find((instrument) => instrument.id === id);
}

/**
 * Is `id` an instrument this codebase may *never* render item content or a scoring algorithm for —
 * either because it is commercially licensed (no license held) or because it is `other` (unknown, so
 * treated the same as licensed per this task's own "if in doubt, treat as licensed" instruction)?
 * `promis_global10`/`promis_pf10a` are the only two ids this returns `false` for, and even for those
 * two this module still stores no content — see this file's header, reason 2.
 */
export function requiresExternalLicenseForContent(id: string): boolean {
  const instrument = proInstrumentById(id);
  if (instrument === undefined) return true; // unrecognised — fail toward "treat as licensed."
  return instrument.licenseStatus === 'commercially_licensed';
}
